"use client";
import React, { Suspense } from "react";
import { BACKEND_SECTIONS, ARCHITECTURE_SECTIONS, DocSectionData, DocFeature } from "../data";
import { CheckCircle2 } from "lucide-react";

const BackendDocsContent = () => {
    return (
        <div className="flex min-h-screen bg-[var(--primaryBackground)] px-4 lg:px-8">
            <div className="flex-1 py-8 min-h-screen max-w-7xl mx-auto">
                <div className="animate-in fade-in slide-in-from-bottom-4 duration-700">
                    {/* Hero Section */}
                    <div className="relative w-full overflow-hidden rounded-3xl bg-surface/40 backdrop-blur-md border border-border/50 p-8 lg:p-12 mb-12 group">
                        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 group-hover:bg-primary/20 transition-colors duration-700" />
                        <div className="absolute bottom-0 left-0 w-64 h-64 bg-secondary/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

                        <div className="relative z-10 max-w-3xl">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-bold uppercase tracking-wider mb-6">
                                Backend Architecture
                            </div>

                            <h1 className="text-4xl lg:text-6xl font-bold text-primary-text mb-6 leading-tight">
                                Build Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-purple-600">Backend</span>
                            </h1>

                            <p className="text-lg text-secondary-text leading-relaxed mb-8 max-w-2xl">
                                Design complete backend architectures with <span className="text-primary-text font-semibold">visual tools</span> and <span className="text-primary-text font-semibold">AI assistance</span>.
                                Create endpoints, services, functions, and middlewares - then export production-ready code.
                            </p>
                        </div>
                    </div>

                    <div className="space-y-32">
                        {/* Backend Sections */}
                        <div>
                            <div className="flex items-center gap-4 mb-12">
                                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-border to-transparent" />
                                <span className="text-sm font-bold text-muted-foreground uppercase tracking-widest">Backend Entities</span>
                                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-border to-transparent" />
                            </div>
                            <div className="space-y-24">
                                {BACKEND_SECTIONS.map((section) => (
                                    <DocSection key={section.id} data={section} />
                                ))}
                            </div>
                        </div>

                        {/* Architecture Sections */}
                        <div>
                            <div className="flex items-center gap-4 mb-12">
                                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-border to-transparent" />
                                <span className="text-sm font-bold text-muted-foreground uppercase tracking-widest">Architecture & Code Generation</span>
                                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-border to-transparent" />
                            </div>
                            <div className="space-y-24">
                                {ARCHITECTURE_SECTIONS.map((section) => (
                                    <DocSection key={section.id} data={section} />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const DocSection = ({ data }: { data: DocSectionData }) => {
    return (
        <section id={data.id} className="scroll-mt-32 group">
            <div className="flex items-center gap-4 mb-8">
                {data.icon && (
                    <div className="p-3 bg-primary/10 rounded-2xl group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-primary/20">
                        <data.icon className="w-8 h-8 text-primary" />
                    </div>
                )}
                <div>
                    <h2 className="text-3xl lg:text-4xl font-bold text-primary-text tracking-tight">{data.title}</h2>
                    <p className="text-lg text-secondary-text mt-2">{data.description}</p>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-8">
                {data.features.map((feature, idx) => (
                    <FeatureCard key={idx} feature={feature} />
                ))}
            </div>
        </section>
    );
};

const FeatureCard = ({ feature }: { feature: DocFeature }) => {
    if (feature.fullWidthComponent && feature.interactiveComponent) {
        return (
            <div className="w-full">
                {feature.interactiveComponent}
            </div>
        );
    }

    return (
        <div className="bg-surface/40 backdrop-blur-md border border-border/50 rounded-3xl overflow-hidden hover:border-primary/50 transition-all duration-500 hover:shadow-2xl hover:shadow-primary/5 group/card">
            <div className={`flex flex-col ${feature.imageOnLeft ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-8 p-6 lg:p-8`}>

                {/* Content Side */}
                <div className="flex-1 space-y-6">
                    <div>
                        <h3 className="text-2xl font-bold text-primary-text mb-3 group-hover/card:text-primary transition-colors">
                            {feature.title}
                        </h3>
                        <p className="text-secondary-text leading-relaxed text-lg">
                            {feature.description}
                        </p>
                    </div>

                    <ul className="space-y-3">
                        {feature.points.map((point, i) => (
                            <li key={i} className="flex items-start gap-3 text-secondary-text">
                                <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                                <span>{point}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Visual Side */}
                {(feature.image || feature.interactiveComponent) && (
                    <div className="flex-1 min-w-0">
                        {feature.interactiveComponent ? (
                            <div className="h-full flex items-center justify-center">
                                {feature.interactiveComponent}
                            </div>
                        ) : (
                            <div className="relative rounded-2xl overflow-hidden border border-border/50 shadow-xl bg-background/50 group-hover/card:scale-[1.02] transition-transform duration-500">
                                {feature.image}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const BackendDocsPage = () => {
    return (
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center text-secondary-text">Loading documentation...</div>}>
            <BackendDocsContent />
        </Suspense>
    );
};

export default BackendDocsPage;
